// SHCT-871 — the 크기 dropdown's behaviour, shared by the three block editors
// (관리지침 · 건강백과 · 상담매뉴얼), which PRD 10 §3.7 governs as one toolbar.
//
// Two things the dropdown has to do beyond firing a command:
//   · stay disabled while the caret is inside a 소제목 — §3.7 fixes that size, so offering
//     four options there would let the author set a value the spec says cannot exist;
//   · show the size actually written at the caret. The first pass reset the dropdown to 기본
//     after every apply, reasoning that the size is per-selection rather than editor state.
//     That made the control lie — text at 크게 read as 기본 — and worse, it disabled 기본
//     itself: a <select> fires no change event when the option already displayed is picked
//     again, so every click on 기본 was swallowed and the text kept the app's 14px fallback.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ParagraphTag } from '@/shared/utils/rich-text-format';
import {
  DEFAULT_TEXT_SIZE,
  SIZE_LOCKED_TAG,
  applyTextSize,
  isInsideTag,
  paragraphAtCaret,
  sizeAtCaret,
  sizeByKey,
} from '@/shared/utils/rich-text-format';

export function useRichTextSize(
  contentRef: React.RefObject<HTMLElement>,
  commit: () => void,
) {
  const [sizeLocked, setSizeLocked] = useState(false);
  const [sizeKey, setSizeKey] = useState<string>(DEFAULT_TEXT_SIZE.key);
  // The 문단 dropdown's value. Held here rather than left uncontrolled so it names the block the
  // caret is in; an uncontrolled dropdown kept the last value the author picked and went on
  // reading 소제목 after Enter had started a new 본문 line.
  const [paragraphTag, setParagraphTag] = useState<ParagraphTag>('p');
  // `commit` is redefined on every render of the host editor; keeping it in a ref lets the
  // selection listener be attached once instead of re-subscribing on each keystroke.
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // Recompute both pieces of state from wherever the selection is right now.
  //
  // Listened to via `selectionchange`, a document-level event with no element-level equivalent that
  // fires for arrow keys, clicks and programmatic moves alike. The containment check keeps another
  // editor's (or the page's) selection from driving this one's dropdown.
  //
  // Exposed as well as listened to, because `selectionchange` does not cover every way the caret's
  // CONTEXT changes. `document.execCommand('formatBlock', 'h2')` turns the line under the caret
  // into a 소제목 without moving the selection, so no selectionchange is guaranteed and the lock
  // would stay open on the one route an author actually takes to a heading: pick 소제목 from the
  // 문단 dropdown, then reach for the size. QC failed the re-test on exactly that. Every toolbar
  // command calls this afterwards; switching back to 본문 unlocks by the same path.
  const refresh = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const sel = window.getSelection();
    const inside =
      sel !== null &&
      sel.rangeCount > 0 &&
      el.contains(sel.getRangeAt(0).commonAncestorContainer);
    if (!inside) return;
    // Two different questions, deliberately kept apart: the size lock follows the whole SELECTION
    // (a drag from 본문 into a 소제목 locks it), the 문단 value follows the CARET.
    setSizeLocked(isInsideTag(el, SIZE_LOCKED_TAG));
    setSizeKey(sizeAtCaret(el).key);
    setParagraphTag(paragraphAtCaret(el));
  }, [contentRef]);

  useEffect(() => {
    document.addEventListener('selectionchange', refresh);
    return () => document.removeEventListener('selectionchange', refresh);
  }, [refresh]);

  const applySize = useCallback(
    (key: string) => {
      const el = contentRef.current;
      if (!el) return;
      el.focus();
      if (applyTextSize(el, sizeByKey(key))) commitRef.current();
      setSizeKey(key);
    },
    [contentRef],
  );

  return { sizeKey, sizeLocked, paragraphTag, applySize, refresh };
}
